import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
  Button,
  ButtonText,
  HStack,
  Icon,
  Menu,
  MenuItem,
  MenuItemLabel,
  Pressable,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { MoreVertical } from 'lucide-react-native';

import { PersonPreviewReach, PersonReachChips } from './PersonReachChips';

export type PersonRowState =
  | 'none'
  | 'sending'
  | 'pendingIn'
  | 'pendingOut'
  | 'connected'
  | 'error'
  | 'unavailable'
  /** GSS occasion list (UX §6.2): first name + avatar only — no subtitle or actions. */
  | 'occasion';

export type PersonRowPreviewProps = {
  firstName: string;
  initials: string;
  photoUrl?: string | null;
  showPhoto: boolean;
  reach: PersonPreviewReach;
};

export type PersonRowInteractiveProps = {
  firstName: string;
  initials: string;
  photoUrl?: string | null;
  reach?: PersonPreviewReach;
  state: PersonRowState;
  errorMessage?: string;
  rowTestId: string;
  isActionPending?: boolean;
  /** Occasion list (S4): show status subtitle but no trailing actions. */
  readOnly?: boolean;
  onConnect?: () => void;
  onAccept?: () => void;
  onDecline?: () => void;
  onWithdraw?: () => void;
  onRemove?: () => void;
  /** GSS handshake only — open My connections. Chips stay on the address book. */
  onViewContact?: () => void;
  onRetry?: () => void;
};

export type PersonRowProps =
  | (PersonRowPreviewProps & { preview: true })
  | (PersonRowInteractiveProps & { preview?: false });

function isPreviewProps(props: PersonRowProps): props is PersonRowPreviewProps & { preview: true } {
  return props.preview === true;
}

function PersonRowAvatar({
  initials,
  photoUrl,
  displayPhoto,
}: {
  initials: string;
  photoUrl?: string | null;
  displayPhoto: boolean;
}) {
  return (
    <Avatar size="md" bg="$brandBase">
      {displayPhoto && photoUrl ? (
        <AvatarImage source={{ uri: photoUrl }} alt="" testID="person-row-avatar-photo" />
      ) : (
        <AvatarFallbackText color="$textOnBrand" testID="person-row-avatar-initials">
          {initials}
        </AvatarFallbackText>
      )}
    </Avatar>
  );
}

function PersonRowPreview({
  firstName,
  initials,
  photoUrl,
  showPhoto,
  reach,
}: PersonRowPreviewProps) {
  const { t } = useTranslation();
  const displayPhoto = showPhoto && !!photoUrl;
  const hasReach = !!(reach.email || reach.phone || reach.whatsapp);
  const showEmptySubtitle = !hasReach;

  return (
    <HStack
      py="$3"
      space="md"
      alignItems="center"
      minHeight={64}
      borderBottomWidth={1}
      borderColor="$borderLight"
      testID="person-row-preview"
    >
      <PersonRowAvatar initials={initials} photoUrl={photoUrl} displayPhoto={displayPhoto} />
      <VStack flex={1} space="xs">
        <Text size="md" fontWeight="medium" color="$textHeading">{firstName}</Text>
        {showEmptySubtitle ? (
          <Text size="sm" color="$textSubtle" testID="person-row-empty-subtitle">
            {t('people.profile.preview.emptySubtitle')}
          </Text>
        ) : (
          <PersonReachChips reach={reach} interactive={false} />
        )}
      </VStack>
    </HStack>
  );
}

function PersonRowInteractive({
  firstName,
  initials,
  photoUrl,
  reach = {},
  state,
  errorMessage,
  rowTestId,
  isActionPending = false,
  readOnly = false,
  onConnect,
  onAccept,
  onDecline,
  onWithdraw,
  onRemove,
  onViewContact,
  onRetry,
}: PersonRowInteractiveProps) {
  const { t } = useTranslation();
  const displayPhoto = !!photoUrl;

  const subtitle = (() => {
    if (state === 'error') {
      return (
        <Text size="sm" color="$statusError" testID={`${rowTestId}-error`}>
          {errorMessage ?? t('people.row.errorAction')}
        </Text>
      );
    }
    if (state === 'pendingIn') {
      return <Text size="sm" color="$textSubtle">{t('people.row.pendingIn')}</Text>;
    }
    if (state === 'pendingOut') {
      return <Text size="sm" color="$textSubtle">{t('people.row.pendingOut')}</Text>;
    }
    if (state === 'unavailable') {
      return <Text size="sm" color="$textSubtle">{t('people.row.unavailable')}</Text>;
    }
    if (state === 'connected') {
      if (onRemove) {
        return <PersonReachChips reach={reach} interactive />;
      }
      return (
        <Text size="sm" color="$textSubtle" testID={`${rowTestId}-connected`}>
          {t('people.row.connected')}
        </Text>
      );
    }
    if (state === 'sending') {
      return <Text size="sm" color="$textSubtle">{t('people.row.sending')}</Text>;
    }
    if (state === 'occasion' || state === 'none') {
      return null;
    }
    return null;
  })();

  const trailing = (() => {
    if (readOnly || state === 'unavailable') return null;

    if (state === 'error') {
      return (
        <Button
          size="sm"
          variant="outline"
          testID={`${rowTestId}-retry`}
          onPress={onRetry}
          isDisabled={isActionPending}
        >
          <ButtonText>{t('people.row.retry')}</ButtonText>
        </Button>
      );
    }

    if (state === 'pendingIn') {
      return (
        <HStack space="sm" alignItems="center">
          <Button
            size="sm"
            action="secondary"
            testID={`${rowTestId}-accept`}
            onPress={onAccept}
            isDisabled={isActionPending}
          >
            <ButtonText>{t('people.row.accept')}</ButtonText>
          </Button>
          <Button
            size="sm"
            variant="outline"
            testID={`${rowTestId}-decline`}
            onPress={onDecline}
            isDisabled={isActionPending}
          >
            <ButtonText>{t('people.row.decline')}</ButtonText>
          </Button>
        </HStack>
      );
    }

    if (state === 'pendingOut') {
      return (
        <Button
          size="sm"
          variant="link"
          testID={`${rowTestId}-withdraw`}
          onPress={onWithdraw}
          isDisabled={isActionPending}
        >
          <ButtonText>{t('people.row.withdraw')}</ButtonText>
        </Button>
      );
    }

    if (state === 'none') {
      return (
        <Button
          size="sm"
          action="secondary"
          testID={`${rowTestId}-connect`}
          onPress={onConnect}
          isDisabled={isActionPending}
        >
          <ButtonText>{t('people.row.connect')}</ButtonText>
        </Button>
      );
    }

    if (state === 'sending') {
      return (
        <Button
          size="sm"
          action="secondary"
          testID={`${rowTestId}-connect`}
          isDisabled
        >
          <ButtonText>{t('people.row.connect')}</ButtonText>
        </Button>
      );
    }

    if (state === 'connected') {
      if (onRemove) {
        return (
          <Menu
            placement="bottom right"
            trigger={({ ...triggerProps }) => (
              <Pressable
                {...triggerProps}
                accessibilityLabel={t('people.row.menuRemove')}
                testID={`${rowTestId}-menu`}
                p="$1"
                borderRadius="$full"
                $hover={{ bg: '$bgHover' }}
              >
                <Icon as={MoreVertical} size="sm" color="$textSubtle" />
              </Pressable>
            )}
          >
            <MenuItem
              key="remove"
              textValue={t('people.row.menuRemove')}
              testID={`${rowTestId}-remove`}
              onPress={onRemove}
            >
              <MenuItemLabel size="sm" color="$statusError">
                {t('people.row.menuRemove')}
              </MenuItemLabel>
            </MenuItem>
          </Menu>
        );
      }

      if (onViewContact) {
        return (
          <Button
            size="sm"
            variant="link"
            testID={`${rowTestId}-view-contact`}
            onPress={onViewContact}
            isDisabled={isActionPending}
          >
            <ButtonText>{t('people.row.viewContact')}</ButtonText>
          </Button>
        );
      }

      return null;
    }

    return null;
  })();

  return (
    <HStack
      py="$3"
      space="md"
      alignItems="center"
      minHeight={64}
      borderBottomWidth={1}
      borderColor="$borderLight"
      testID="person-row"
    >
      <PersonRowAvatar initials={initials} photoUrl={photoUrl} displayPhoto={displayPhoto} />
      <VStack flex={1} space="xs">
        <Text size="md" fontWeight="medium" color="$textHeading">{firstName}</Text>
        {subtitle}
      </VStack>
      {trailing}
    </HStack>
  );
}

/**
 * Shared person row — preview mode on Profile omits trailing actions.
 */
export function PersonRow(props: PersonRowProps) {
  if (isPreviewProps(props)) {
    return <PersonRowPreview {...props} />;
  }
  return <PersonRowInteractive {...props} />;
}

/** Derive two-letter initials from a first name only (connection peers). */
export function peerInitials(firstName: string): string {
  const trimmed = (firstName ?? '').trim();
  if (!trimmed) return '?';
  if (trimmed.length === 1) return trimmed.toUpperCase();
  return trimmed.slice(0, 2).toUpperCase();
}
